// Card designs. 'classic' lives in /cards (root); every other deck lives in /cards/<id>/ with the same file names.
export const CARD_DECKS = [
  { id: 'classic', name: 'Classic' },
  { id: 'design2', name: 'Design 2' },
];
const DECK_KEY = 'jv_card_deck';
let activeDeck = 'classic';
try {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(DECK_KEY) : null;
  if (saved && CARD_DECKS.some((d) => d.id === saved)) activeDeck = saved;
} catch (e) { /* ignore */ }

class CardUtils {
  static SUITS = ['hearts', 'diamonds', 'clubs', 'spades'];

  static getDeck() { return activeDeck; }
  static setDeck(id) {
    if (!CARD_DECKS.some((d) => d.id === id)) return;
    activeDeck = id;
    try { localStorage.setItem(DECK_KEY, id); } catch (e) { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent('card-deck-changed', { detail: id })); } catch (e) { /* ignore */ }
  }
  static getDeckBase(id = activeDeck) { return id === 'classic' ? '/cards' : `/cards/${id}`; }
  static RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
  
  // Fixed value mapping
  static VALUES = {
    '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10,
    'J': 11, 'Q': 12, 'K': 13, 'A': 14
  };

  // Auto arrange using your grouping system
  static autoArrangeCards(cards) {
    // Remove any existing groups and flatten cards
    const flatCards = cards.flatMap(card => 
      card.isGroup ? card.cards : [card]
    ).filter(card => !card.isGroupSeparator);
    
    // Shuffle cards for initial randomness
    const shuffledCards = this._shuffleArray([...flatCards]);
    
    // Create 6 groups with proper structure
    const groups = {
      hearts: { type: 'hearts', cards: [], displayName: 'Hearts' },
      spades: { type: 'spades', cards: [], displayName: 'Spades' },
      diamonds: { type: 'diamonds', cards: [], displayName: 'Diamonds' },
      clubs: { type: 'clubs', cards: [], displayName: 'Clubs' },
      wildJokers: { type: 'wildJokers', cards: [], displayName: 'Jokers' },
      sequences: { type: 'sequences', cards: [], displayName: 'Sequences' }
    };
    
    // Separate jokers first (using rank '2' as per your system)
    const jokers = shuffledCards.filter(card => card.rank === '2');
    const nonJokers = shuffledCards.filter(card => card.rank !== '2');
    
    // Sort non-jokers by suit and value
    nonJokers.forEach(card => {
      switch(card.suit) {
        case 'hearts': groups.hearts.cards.push(card); break;
        case 'spades': groups.spades.cards.push(card); break;
        case 'diamonds': groups.diamonds.cards.push(card); break;
        case 'clubs': groups.clubs.cards.push(card); break;
        default: groups.sequences.cards.push(card);
      }
    });
    
    // Add jokers to wildJokers group
    groups.wildJokers.cards = jokers;
    
    // Sort each group by card value
    Object.values(groups).forEach(group => {
      group.cards = group.cards.sort((a, b) => a.value - b.value);
    });
    
    // Create arranged cards with visible group separators
    const arrangedCards = [];
    const groupOrder = ['hearts', 'spades', 'diamonds', 'clubs', 'wildJokers', 'sequences'];
    
    groupOrder.forEach((groupKey, index) => {
      const group = groups[groupKey];
      
      // Add group separator (except before first group)
      if (index > 0 && group.cards.length > 0) {
        arrangedCards.push({
          isGroupSeparator: true,
          id: `separator-${groupKey}`,
          groupType: group.type,
          displayName: group.displayName,
          cardCount: group.cards.length
        });
      }
      
      // Add cards from this group
      arrangedCards.push(...group.cards);
    });
    
    return arrangedCards;
  }

  // Fisher-Yates shuffle algorithm for true randomness
  static _shuffleArray(array) {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }

  // Create card group
  static createCardGroup(selectedCards, playerCards) {
    if (selectedCards.length < 3) {
      console.log('Need at least 3 cards to form a group');
      return playerCards;
    }
    
    // Validate if selected cards can form a valid group
    if (!this.isValidGroup(selectedCards).isValid) {
      console.log('Selected cards cannot form a valid group');
      return playerCards;
    }
    
    // Remove selected cards from main array (including separators)
    const remainingCards = playerCards.filter(card => 
      !selectedCards.some(selected => selected.id === card.id) && 
      !card.isGroupSeparator
    );
    
    // Create new group with proper metadata
    const newGroup = {
      isGroup: true,
      cards: [...selectedCards],
      id: `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      groupType: this._getGroupType(selectedCards),
      isValid: true,
      createdAt: Date.now()
    };
    
    console.log(`Created new ${newGroup.groupType} group with ${selectedCards.length} cards`);
    
    // Add group to remaining cards at logical position
    return this._insertGroupAtPosition(remainingCards, newGroup);
  }

  static _insertGroupAtPosition(cards, newGroup) {
    // Find the position to insert (after the last group or at the end)
    const lastGroupIndex = cards.map((card, index) => 
      card.isGroup ? index : -1
    ).filter(index => index !== -1).pop();
    
    if (lastGroupIndex !== undefined) {
      return [
        ...cards.slice(0, lastGroupIndex + 1),
        newGroup,
        ...cards.slice(lastGroupIndex + 1)
      ];
    }
    
    // If no existing groups, add to the end
    return [...cards, newGroup];
  }

  static _getGroupType(cards) {
    const suits = cards.map(card => card.suit);
    const ranks = cards.map(card => card.rank);
    const uniqueSuits = [...new Set(suits)];
    const uniqueRanks = [...new Set(ranks)];
    
    if (uniqueSuits.length === 1) {
      // NEW: Identical cards (same rank + same suit) → duplicate pure sequence
      // (multi-deck variant), e.g., 2C,2C,2C.
      if (uniqueRanks.length === 1) return 'sequence';
      // Check if it's a sequence
      const sortedCards = [...cards].sort((a, b) => a.value - b.value);
      const isSequence = sortedCards.every((card, index, array) => 
        index === 0 || card.value === array[index - 1].value + 1
      );
      
      return isSequence ? 'sequence' : 'set';
    } else if (uniqueRanks.length === 1) {
      return 'set';
    } else {
      return 'mixed';
    }
  }

  // Enhanced group validation (mirrors the backend GameRules: a pure sequence,
  // an impure sequence and a set are all valid — and wild jokers are treated as
  // jokers just like printed jokers).
  static isValidGroup(cards, wildJoker = null, burnedJokerIds = null) {
    if (!cards || cards.length < 3) {
      return { isValid: false, reason: 'Need at least 3 cards' };
    }
    
        const suits = cards.map(card => card.suit);
    const ranks = cards.map(card => card.rank);
    const values = cards.map(card => card.value);
    const uniqueSuits = [...new Set(suits)];
    const uniqueRanks = [...new Set(ranks)];
    
    // Identify jokers: printed jokers AND wild jokers (cards matching the wild
    // joker rank). Burned jokers (discarded wild jokers picked up from the
    // discard pile) have lost their joker status and count as natural cards.
    const isJokerFn = (card) => {
      if (card.isJoker || card.suit === 'joker' || card.rank === 'Joker') return true;
      if (!wildJoker) return false;
      if (burnedJokerIds && burnedJokerIds.has(card.id)) return false;
      // If the wild joker is a printed joker, all 2's become wild jokers.
      if (wildJoker.isJoker || wildJoker.suit === 'joker' || wildJoker.rank === 'Joker') {
        return card.rank === '2';
      }
      // Otherwise, any card matching the wild joker rank is a wild joker.
      return card.rank === wildJoker.rank;
    };
    const jokerCards = cards.filter(isJokerFn);
    const naturalCards = cards.filter(c => !isJokerFn(c));
    const jokerCount = jokerCards.length;
    
    // NEW: All printed jokers of the same (red) colour = pure sequence.
    // Black printed jokers do NOT form a pure sequence.
    if (naturalCards.length === 0 && jokerCards.length >= 3) {
      const allPrinted = jokerCards.every(c => c.isJoker || c.suit === 'joker' || c.rank === 'Joker');
      const allRed = jokerCards.every(c => c.isRed === true);
      if (allPrinted && allRed) {
        return { isValid: true, type: 'sequence', isPure: true, duplicate: true };
      }
      // NEW RULE: 3+ jokers (wild or printed, any colour) = valid impure sequence
      return { isValid: true, type: 'sequence', isPure: false, jokersOnly: true };
    }
    
    if (naturalCards.length === 0) {
      return { isValid: false, reason: 'Need at least one natural card' };
    }
    
    const naturalSuits = naturalCards.map(c => c.suit);
    const naturalRanks = naturalCards.map(c => c.rank);
    const naturalValues = naturalCards.map(c => c.value);
    const uniqueNaturalSuits = [...new Set(naturalSuits)];
    const uniqueNaturalRanks = [...new Set(naturalRanks)];
    
    // NEW: Duplicate/identical sequence (same rank + same suit), e.g., 2C,2C,2C.
    // This is a valid PURE sequence in the multi-deck variant.
    if (jokerCount === 0 && uniqueNaturalRanks.length === 1 && uniqueNaturalSuits.length === 1) {
      return { isValid: true, type: 'sequence', isPure: true, duplicate: true };
    }
    
    // Check for set (same rank, different suits)
    if (uniqueNaturalRanks.length === 1) {
      const uniqueNaturalSuitsSet = new Set(naturalSuits);
      if (uniqueNaturalSuitsSet.size + jokerCount >= cards.length) {
        return { isValid: true, type: 'set' };
      }
    }
    
    // Check for sequence (same suit, consecutive values with jokers filling gaps)
    if (uniqueNaturalSuits.length === 1) {
      const sortedValues = [...naturalValues].sort((a, b) => a - b);
      const isConsecutive = sortedValues.every((value, index, array) => 
        index === 0 || value === array[index - 1] + 1
      );
      
      if (isConsecutive) {
        return { isValid: true, type: 'sequence' };
      }
      
      // Check for sequence with jokers filling gaps
      if (jokerCount > 0) {
        let gaps = 0;
        for (let i = 1; i < sortedValues.length; i++) {
          const gap = sortedValues[i] - sortedValues[i - 1] - 1;
          if (gap > 0) gaps += gap;
        }
        
        if (gaps <= jokerCount) {
          return { isValid: true, type: 'sequence', hasJokers: true };
        }
        
        // Try Ace-as-low fallback
        if (sortedValues.includes(14)) {
          const aceLowValues = sortedValues.map(v => v === 14 ? 1 : v).sort((a, b) => a - b);
          let aceGaps = 0;
          for (let i = 1; i < aceLowValues.length; i++) {
            const gap = aceLowValues[i] - aceLowValues[i - 1] - 1;
            if (gap > 0) aceGaps += gap;
          }
          if (aceGaps <= jokerCount) {
            return { isValid: true, type: 'sequence', hasJokers: true };
          }
        }
      }
    }
    
    return { isValid: false, reason: 'Cards do not form a valid set or sequence' };
  }

  // Manual card arrangement
  static manualArrangeCards(cards, draggedCardId, targetPosition) {
    const cardIndex = cards.findIndex(card => card.id === draggedCardId);
    if (cardIndex === -1) return cards;
    
    const newCards = [...cards];
    const [draggedCard] = newCards.splice(cardIndex, 1);
    
    // Insert at target position, avoiding separators
    let insertIndex = targetPosition;
    while (insertIndex < newCards.length && newCards[insertIndex].isGroupSeparator) {
      insertIndex++;
    }
    
    newCards.splice(insertIndex, 0, draggedCard);
    return newCards;
  }

  // Toggle card selection for grouping
  static toggleCardSelection(cardId, selectedCards, isCtrlPressed) {
    if (!isCtrlPressed) {
      // Single selection (for discard)
      return [cardId];
    } else {
      // Multi-selection for grouping
      if (selectedCards.includes(cardId)) {
        return selectedCards.filter(id => id !== cardId);
      } else {
        return [...selectedCards, cardId];
      }
    }
  }

  // Break existing group
  static breakCardGroup(groupId, playerCards) {
    const groupIndex = playerCards.findIndex(card => card.id === groupId && card.isGroup);
    if (groupIndex === -1) return playerCards;
    
    const group = playerCards[groupIndex];
    const cardsBefore = playerCards.slice(0, groupIndex);
    const cardsAfter = playerCards.slice(groupIndex + 1);
    
    return [...cardsBefore, ...group.cards, ...cardsAfter];
  }

    /**
   * Determine if a card is a joker (wild or printed).
   *
   * @param {Object} card           - The card to check
   * @param {Object} wildJokerCard  - The selected wild joker card
   * @param {string} [contextSuit]  - If provided, a card matching the wild joker rank
   *                                  but with this suit is considered NATURAL (not a joker)
   *                                  because it appears in its natural position.
   *                                  E.g., wild joker=9♠, card=9♥, contextSuit='hearts' → NOT a joker
   *                                  E.g., wild joker=9♠, card=9♦, contextSuit='hearts' → IS a joker
   */
  static isJoker(card, wildJokerCard, contextSuit = null) {
    if (!card) return false;

    // Printed jokers (always jokers)
    if (card.isJoker || card.suit === 'joker') {
      return true;
    }

    // If no wildJokerCard provided, can't determine wild jokers
    if (!wildJokerCard) return false;

    // If the card IS the exact wild joker card in its natural position, it's NOT a joker
    if (card.rank === wildJokerCard.rank && card.suit === wildJokerCard.suit) {
      return false; // Natural wild joker is NOT a joker
    }

    // If wild joker is a printed joker, then all 2's become wild jokers
    if (wildJokerCard.isJoker || wildJokerCard.suit === 'joker') {
      // But if contextSuit matches the card's suit, it's in its natural position → NOT a joker
      if (card.rank === '2') {
        if (contextSuit && card.suit === contextSuit) return false;
        return true;
      }
      return false;
    }

    // Normal case: cards matching wild joker rank are wild jokers
    // EXCEPT the exact wild joker card itself (which we already handled above)
    if (card.rank === wildJokerCard.rank) {
      // If context suit is provided and matches this card's suit,
      // the card is in its natural position → NOT a joker
      if (contextSuit && card.suit === contextSuit) return false;

      return true;
    }

    return false;
  }

  // Get joker type for display
  static getJokerType(card, wildJokerCard) {
    if (!card) return null;
    
    // Printed joker
    if (card.isJoker || card.suit === 'joker') {
      return 'printed';
    }
    
    // Wild joker
    if (this.isJoker(card, wildJokerCard) && !card.isJoker) {
      return 'wild';
    }
    
    return null;
  }

  // Count total available jokers in the game
  static countAvailableJokers(deck, wildJokerCard) {
    if (!wildJokerCard) return 0;
    
    let printedJokers = 0;
    let wildJokers = 0;
    
    deck.forEach(card => {
      // Count printed jokers (always available)
      if (card.isJoker || card.suit === 'joker') {
        printedJokers++;
      }
      // Count wild jokers
      else if (this.isJoker(card, wildJokerCard)) {
        wildJokers++;
      }
    });
    
    console.log(`Joker Count: ${printedJokers} printed + ${wildJokers} wild = ${printedJokers + wildJokers} total`);
    return printedJokers + wildJokers;
  }

  // Create deck with 2 decks + 4 jokers = 108 cards
  static createDeck(includeJokers = true) {
    const deck = [];
    
    // Generate first deck
    for (const suit of this.SUITS) {
      for (const rank of this.RANKS) {
        deck.push({
          id: `1_${rank}_${suit}`,
          suit,
          rank,
          value: this.VALUES[rank],
          isRed: suit === 'hearts' || suit === 'diamonds',
          deck: 1
        });
      }
    }
    
    // Generate second deck
    for (const suit of this.SUITS) {
      for (const rank of this.RANKS) {
        deck.push({
          id: `2_${rank}_${suit}`,
          suit,
          rank,
          value: this.VALUES[rank],
          isRed: suit === 'hearts' || suit === 'diamonds',
          deck: 2
        });
      }
    }
    
    // Add 4 printed jokers
    if (includeJokers) {
      for (let i = 1; i <= 4; i++) {
        deck.push({
          id: `joker_${i}`,
          suit: 'joker',
          rank: 'Joker',
          value: 15,
          isRed: i <= 2,
          isJoker: true,
          deck: 0
        });
      }
    }
    
    return this.shuffleDeck(deck);
  }

  // Shuffle deck
  static shuffleDeck(deck) {
    const shuffled = [...deck];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  // Deal cards to players
  static dealCards(deck, playerCount, cardsPerPlayer) {
    const hands = [];
    const remainingDeck = [...deck];
    
    for (let i = 0; i < playerCount; i++) {
      const hand = remainingDeck.splice(0, cardsPerPlayer);
      hands.push(hand);
    }
    
    return {
      hands,
      remainingDeck
    };
  }

  // Sort hand by suit and rank
  static sortHand(cards) {
    return [...cards].sort((a, b) => {
      const suitOrder = this.SUITS.indexOf(a.suit) - this.SUITS.indexOf(b.suit);
      if (suitOrder !== 0) return suitOrder;
      return this.RANKS.indexOf(a.rank) - this.RANKS.indexOf(b.rank);
    });
  }

  // Get card image URL - FIXED: Added proper error handling
  static getCardImage(card) {
    // Add comprehensive null/undefined checks
    if (!card) {
      console.warn('Card is undefined in getCardImage');
      return CardUtils.getDeckBase() + "/card_back.png";
    }
    
    // Handle group separators - no image
    if (card.isGroupSeparator) {
      return null;
    }
    
    // Handle joker cards first
    if (card.isJoker || card.suit === 'joker') {
      return card.isRed ? CardUtils.getDeckBase() + "/red_joker.png" : CardUtils.getDeckBase() + "/black_joker.png";
    }
    
    // Validate card has required properties
    if (!card.suit || !card.rank) {
      console.warn('Card missing suit or rank:', card);
      return CardUtils.getDeckBase() + "/card_back.png";
    }
    
    try {
      // Use safe property access with fallbacks
      const rank = (card.rank || '').toString().toLowerCase();
      const suit = (card.suit || '').toString().toLowerCase();
      
      // Map ranks to file names (most card decks use this format)
      const rankMap = {
        '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9', '10': '10',
        'j': 'jack', 'q': 'queen', 'k': 'king', 'a': 'ace'
      };
      
      const fileRank = rankMap[rank] || rank;
      return `${CardUtils.getDeckBase()}/${fileRank}_of_${suit}.png`;
    } catch (error) {
      console.error('Error generating card image path:', error, card);
      return CardUtils.getDeckBase() + "/card_back.png";
    }
  }

    // Get fallback HTML for a card when image fails to load
  static getFallbackHTML(card) {
    if (!card) return '<span>?</span>';
    const suitSymbol = this.getSuitSymbol(card);
    const valueDisplay = this.getValueDisplay(card);
    const isRed = card.isRed || card.suit === 'hearts' || card.suit === 'diamonds';
    const color = isRed ? '#ff4444' : '#ddd';
    return `<span style="font-size:9px;color:${color};display:flex;align-items:center;justify-content:center;height:100%;background:#1a1a2e;border-radius:2px;font-weight:bold">${valueDisplay}${suitSymbol}</span>`;
  }

  // Get card back image
  static getCardBackImage() {
    return CardUtils.getDeckBase() + '/card_back.png';
  }

  // Handle image errors - FIXED: Added error handling
  static handleImageError(e, card) {
    if (!card || card.isGroupSeparator) return;
    
    console.warn('Card image failed to load:', e.target.src);
    
    try {
      // Create fallback visual
      const cardElement = e.target.parentElement;
      if (!cardElement) return;
      
      const fallback = document.createElement('div');
      fallback.className = 'card-fallback';
      
      const suitSymbol = this.getSuitSymbol(card);
      const valueDisplay = this.getValueDisplay(card);
      const colorClass = (card && (card.isRed || card.suit === 'hearts' || card.suit === 'diamonds')) ? 'red' : 'black';
      
      fallback.innerHTML = `
        <div class="card-top">
          <span class="card-value">${valueDisplay}</span>
          <span class="card-suit">${suitSymbol}</span>
        </div>
        <div class="card-center">
          <span class="card-suit-large">${suitSymbol}</span>
        </div>
      `;
      fallback.classList.add(colorClass);
      
      cardElement.appendChild(fallback);
      e.target.style.display = 'none';
    } catch (error) {
      console.error('Error in handleImageError:', error);
    }
  }

  static getSuitSymbol(card) {
    if (!card) return '?';
    switch(card.suit) {
      case 'hearts': return '♥';
      case 'diamonds': return '♦';
      case 'clubs': return '♣';
      case 'spades': return '♠';
      case 'joker': return '🃏';
      default: return card.suit ? card.suit.charAt(0).toUpperCase() : '?';
    }
  }

  static getValueDisplay(card) {
    if (!card) return '?';
    if (card.rank === 'Joker') return 'JOK';
    return card.rank || '?';
  }

  // Validate if hand can declare
  static canDeclare(cards) {
    // Filter out separators and groups for declaration validation
    const playableCards = cards.filter(card => 
      !card.isGroupSeparator && !card.isGroup
    );
    
    const sequences = this.findSequences(playableCards);
    const sets = this.findSets(playableCards);
    
    // Check for at least 2 sequences (one pure)
    if (sequences.length < 2) return false;
    
    // Check for pure sequence (without joker)
    const hasPureSequence = sequences.some(seq => 
      seq.every(card => !card.isJoker)
    );
    
    if (!hasPureSequence) return false;
    
    // Check if all cards are used in valid sequences/sets
    const allUsedCards = new Set();
    sequences.forEach(seq => seq.forEach(card => allUsedCards.add(card.id)));
    sets.forEach(set => set.forEach(card => allUsedCards.add(card.id)));
    
    return allUsedCards.size === playableCards.length;
  }

  // Find sequences in hand (kept for declaration validation)
  static findSequences(cards) {
    const sequences = [];
    const suits = [...new Set(cards.map(card => card.suit))];
    
    suits.forEach(suit => {
      const suitCards = cards.filter(card => card.suit === suit)
                           .sort((a, b) => this.RANKS.indexOf(a.rank) - this.RANKS.indexOf(b.rank));
      
      let currentSequence = [];
      
      for (let i = 0; i < suitCards.length; i++) {
        if (currentSequence.length === 0) {
          currentSequence.push(suitCards[i]);
        } else {
          const lastCard = currentSequence[currentSequence.length - 1];
          const currentCard = suitCards[i];
          const lastIndex = this.RANKS.indexOf(lastCard.rank);
          const currentIndex = this.RANKS.indexOf(currentCard.rank);
          
          if (currentIndex === lastIndex + 1) {
            currentSequence.push(currentCard);
          } else {
            if (currentSequence.length >= 3) {
              sequences.push([...currentSequence]);
            }
            currentSequence = [currentCard];
          }
        }
      }
      
      if (currentSequence.length >= 3) {
        sequences.push([...currentSequence]);
      }
    });
    
    // NEW: Find duplicate/identical sequences (same rank + same suit), e.g., 2C,2C,2C.
    // This is a valid pure sequence in the multi-deck variant.
    suits.filter(s => s !== 'joker').forEach(suit => {
      const suitCards = cards.filter(card => card.suit === suit);
      const byRank = {};
      suitCards.forEach(card => {
        (byRank[card.rank] = byRank[card.rank] || []).push(card);
      });
      Object.values(byRank).forEach(group => {
        if (group.length >= 3) {
          sequences.push([...group]);
        }
      });
    });
    
    return sequences;
  }

  // Find sets in hand (kept for declaration validation)
  static findSets(cards) {
    const sets = [];
    const rankGroups = {};
    
    cards.forEach(card => {
      if (!rankGroups[card.rank]) {
        rankGroups[card.rank] = [];
      }
      rankGroups[card.rank].push(card);
    });
    
    Object.values(rankGroups).forEach(group => {
      if (group.length >= 3) {
        // Ensure different suits
        const suits = new Set();
        const validSet = [];
        
        group.forEach(card => {
          if (!suits.has(card.suit)) {
            suits.add(card.suit);
            validSet.push(card);
          }
        });
        
        if (validSet.length >= 3) {
          sets.push(validSet);
        }
      }
    });
    
    return sets;
  }

  // Validate declaration
  static validateDeclaration(cards, sequences, sets) {
    const allUsedCards = new Set();
    let totalSequences = 0;
    let totalSets = 0;
    
    // Validate sequences
    for (const sequence of sequences) {
      if (sequence.length < 3) {
        return { isValid: false, error: 'Sequence must have at least 3 cards' };
      }
      
      const suit = sequence[0].suit;
      const allSameSuit = sequence.every(card => card.suit === suit);
      const rankIndexes = sequence.map(card => this.RANKS.indexOf(card.rank));
      const uniqueRankIndexes = [...new Set(rankIndexes)];
      
      // NEW: Duplicate/identical sequence (same rank + same suit), e.g., 2C,2C,2C.
      if (allSameSuit && uniqueRankIndexes.length === 1) {
        totalSequences++;
        sequence.forEach(card => allUsedCards.add(card.id));
        continue;
      }
      
      const sortedRanks = [...rankIndexes].sort((a, b) => a - b);
      
      for (let i = 0; i < sequence.length; i++) {
        if (sequence[i].suit !== suit) {
          return { isValid: false, error: 'Sequence must be same suit' };
        }
        
        if (i > 0 && sortedRanks[i] !== sortedRanks[i-1] + 1) {
          return { isValid: false, error: 'Sequence must be consecutive' };
        }
      }
      
      totalSequences++;
      sequence.forEach(card => allUsedCards.add(card.id));
    }
    
    // Validate sets
    for (const set of sets) {
      if (set.length < 3) {
        return { isValid: false, error: 'Set must have at least 3 cards' };
      }
      
      const rank = set[0].rank;
      const suits = new Set();
      
      for (const card of set) {
        if (card.rank !== rank) {
          return { isValid: false, error: 'Set must be same rank' };
        }
        if (suits.has(card.suit)) {
          return { isValid: false, error: 'Set must have different suits' };
        }
        suits.add(card.suit);
      }
      
      totalSets++;
      set.forEach(card => allUsedCards.add(card.id));
    }
    
    // Check all cards used
    if (allUsedCards.size !== cards.length) {
      return { 
        isValid: false, 
        error: `Not all cards used. ${cards.length - allUsedCards.size} cards remaining` 
      };
    }
    
    // Check minimum sequences
    if (totalSequences < 2) {
      return { isValid: false, error: 'Need at least 2 sequences' };
    }
    
    return {
      isValid: true,
      score: 0,
      sequences: totalSequences,
      sets: totalSets,
      message: 'Valid declaration!'
    };
  }

  // Check if card can be added to sequence
  static canAddToSequence(card, sequence) {
    if (sequence.length === 0) return true;
    
    const suit = sequence[0].suit;
    if (card.suit !== suit) return false;
    
    const sequenceRanks = sequence.map(c => this.RANKS.indexOf(c.rank)).sort((a, b) => a - b);
    const cardRankIndex = this.RANKS.indexOf(card.rank);
    
    return cardRankIndex === sequenceRanks[0] - 1 || 
           cardRankIndex === sequenceRanks[sequenceRanks.length - 1] + 1;
  }

  // Check if card can be added to set
  static canAddToSet(card, set) {
    if (set.length === 0) return true;
    
    const rank = set[0].rank;
    if (card.rank !== rank) return false;
    
    return !set.some(c => c.suit === card.suit);
  }
}

export default CardUtils;